import { useState } from 'react';

import { CONSOLE_COUNT_MAX, formatV1SearchCurl, LANDING_SAMPLE_FORM } from '../../lib/sample';
import type { IssuedApiKey } from '../../types';
import { Button } from '../ui/Button';
import { Note } from '../ui/Note';

type IssuedKeyRevealProps = {
    issued: IssuedApiKey;
    onDismiss: () => void;
    /** Revoke this key immediately (issue was a mistake / discard secret). */
    onDelete: () => void;
    deletePending?: boolean;
};

type CopyKind = 'key' | 'curl' | null;

/**
 * The one moment the full secret exists on screen. `apps/api` never returns it
 * again after this response, so losing it here means revoking and issuing a
 * fresh one, the copy says so rather than letting anyone assume otherwise.
 *
 * Action side: Copy key · Copy as curl · Delete. No other surface reuses the
 * secret — paste into curl yourself later is out of scope on purpose.
 */
export function IssuedKeyReveal({
    issued,
    onDismiss,
    onDelete,
    deletePending = false,
}: IssuedKeyRevealProps) {
    const [copied, setCopied] = useState<CopyKind>(null);

    function flash(kind: CopyKind) {
        setCopied(kind);
        window.setTimeout(() => setCopied(null), 2000);
    }

    function copyKey() {
        void navigator.clipboard.writeText(issued.key).then(() => flash('key'));
    }

    function copyCurl() {
        const origin =
            typeof window !== 'undefined' ? window.location.origin : 'https://actualsold.com';
        const curl = formatV1SearchCurl(origin, LANDING_SAMPLE_FORM, CONSOLE_COUNT_MAX, {
            apiKey: issued.key,
        });
        void navigator.clipboard.writeText(curl).then(() => flash('curl'));
    }

    return (
        <div className="rounded-md border border-primary/40 bg-primary-soft p-4">
            <p className="mb-2 text-sm font-medium text-text">"{issued.apiKey.name}" created</p>
            <div className="flex flex-wrap items-center gap-2">
                {/* `rounded-sm`, not bare `rounded`, the bare utility is a
                    hard-coded 0.25rem in Tailwind v4 and ignores the radius
                    tokens index.css zeroes out, so it was the one curved corner
                    left on the site. */}
                <code className="min-w-0 flex-1 truncate rounded-sm bg-surface px-3 py-2 font-mono text-sm text-text">
                    {issued.key}
                </code>
                <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="secondary" onClick={copyKey} disabled={deletePending}>
                        {copied === 'key' ? 'Copied' : 'Copy'}
                    </Button>
                    <Button size="sm" variant="secondary" onClick={copyCurl} disabled={deletePending}>
                        {copied === 'curl' ? 'Copied curl' : 'Copy as curl'}
                    </Button>
                    <Button
                        size="sm"
                        variant="quiet"
                        disabled={deletePending}
                        onClick={() => {
                            if (
                                confirm(
                                    `Delete "${issued.apiKey.name}"? The key is revoked immediately and this secret is gone.`,
                                )
                            ) {
                                onDelete();
                            }
                        }}
                    >
                        {deletePending ? 'Deleting…' : 'Delete'}
                    </Button>
                </div>
            </div>
            <div className="mt-3">
                <Note tone="warn">
                    Store this now. It will not be shown again. If it's lost, revoke it and issue
                    another.
                </Note>
            </div>
            <button
                type="button"
                onClick={onDismiss}
                disabled={deletePending}
                className="mt-3 text-sm text-text-muted hover:text-text hover:underline disabled:opacity-50"
            >
                Done
            </button>
        </div>
    );
}
